import { useMemo, useState } from 'react';
import {
  useAddMeasurement,
  useMeasurementGuide,
  useMeasurements,
  usePhotos,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate, todayIso } from '../../lib/format';
import { toErrorMessage } from '../../i18n/errorMessage';
import { AchievementsSection } from './AchievementsSection';
import { GoalsSection } from './GoalsSection';
import { WeeklySummarySection } from './WeeklySummarySection';
import { MetricChartSection } from './MetricChartSection';
import { MeasurementGuide, guideStepText } from './MeasurementGuide';
import { ProgressPhotoModal } from './ProgressPhotoModal';
import { comparePair, photoCaption } from '../../lib/photos';
import {
  Button,
  Card,
  CardHeader,
  LineChart,
  MeasurementCard,
  Modal,
  NumberInput,
  PhotoCompare,
  SkeletonCard,
  SkeletonList,
  useToast,
  type ChartPoint,
} from '../../ui';

type CircumferenceField = 'waistCm' | 'chestCm' | 'hipsCm' | 'armCm' | 'thighCm';

/** Polje opsega ↔ korak upute; `stepKey` je isti ključ koji vraća API. */
const CIRCUMFERENCE_FIELDS: readonly {
  field: CircumferenceField;
  labelKey: string;
  stepKey: string;
}[] = [
  { field: 'waistCm', labelKey: 'progress.waist', stepKey: 'waist' },
  { field: 'chestCm', labelKey: 'progress.chest', stepKey: 'chest' },
  { field: 'hipsCm', labelKey: 'progress.hips', stepKey: 'hips' },
  { field: 'armCm', labelKey: 'progress.arm', stepKey: 'arm' },
  { field: 'thighCm', labelKey: 'progress.thigh', stepKey: 'thigh' },
];

export function Progress() {
  const { t, locale } = useTranslation();
  const measurements = useMeasurements();
  const photos = usePhotos();
  const addMeasurement = useAddMeasurement();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [weight, setWeight] = useState<number | ''>('');
  const [circumference, setCircumference] = useState<Record<CircumferenceField, number | ''>>({
    waistCm: '',
    chestCm: '',
    hipsCm: '',
    armCm: '',
    thighCm: '',
  });

  // Uputa se dohvaća tek kad je obrazac otvoren — ekran napretka je ne treba.
  const guide = useMeasurementGuide(open);

  const points: ChartPoint[] = useMemo(() => {
    const items = measurements.data?.items ?? [];
    return items
      .filter((m) => m.weightKg !== null)
      .slice()
      .reverse()
      .map((m) => ({ x: formatDate(`${m.measuredOn}T00:00:00.000Z`, locale), y: m.weightKg! }));
  }, [measurements.data, locale]);

  async function save() {
    /*
      Prazan obrazac je prije putovao do servera i vraćao se kao generička
      greška validacije. Provjera je ista ovdje i tamo, ali korisnik dobije
      rečenicu koja kaže što nedostaje, bez čekanja na mrežu.
    */
    const nothing =
      weight === '' && Object.values(circumference).every((v) => v === '');
    if (nothing) {
      setFormError(t('progress.nothingToSave'));
      return;
    }
    setFormError(null);
    try {
      await addMeasurement.mutateAsync({
        measuredOn: todayIso(),
        ...(weight === '' ? {} : { weightKg: weight }),
        ...Object.fromEntries(
          Object.entries(circumference).filter(([, v]) => v !== ''),
        ),
      });
      setOpen(false);
      setWeight('');
      setCircumference({ waistCm: '', chestCm: '', hipsCm: '', armCm: '', thighCm: '' });
      toast(t('profile.saved'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <>
      <PageHeader
        title={t('progress.title')}
        action={
          <Button size="sm" onClick={() => setOpen(true)}>
            {t('progress.addMeasurement')}
          </Button>
        }
      />

      <div className="flex flex-col gap-6">
        {/* Tjedni sažetak je prvo što klijent traži nakon obavijesti — ide na vrh. */}
        <WeeklySummarySection />

        <AchievementsSection />

        <MetricChartSection />

        <GoalsSection />

        <Card>
          <CardHeader title={t('progress.weightChart')} />
          <QueryBoundary
            query={measurements}
            skeleton={<SkeletonCard />}
            isEmpty={(d) => d.items.length === 0}
            empty={{
              message: t('empty.noMeasurements'),
              actionLabel: t('empty.addFirstMeasurement'),
              onAction: () => setOpen(true),
            }}
          >
            {() =>
              points.length < 2 ? (
                <p className="text-[14.5px] text-ink-muted">{t('progress.noChartData')}</p>
              ) : (
                <LineChart
                  points={points}
                  unit="kg"
                  ariaLabel={t('progress.weightChart')}
                  formatValue={(v) => `${Math.round(v * 10) / 10}`}
                />
              )
            }
          </QueryBoundary>
        </Card>

        <section>
          <h2 className="eyebrow mb-3">{t('progress.measurements')}</h2>
          <QueryBoundary
            query={measurements}
            skeleton={<SkeletonList rows={3} />}
            isEmpty={(d) => d.items.length === 0}
            empty={{
              message: t('empty.noMeasurements'),
              actionLabel: t('empty.addFirstMeasurement'),
              onAction: () => setOpen(true),
            }}
          >
            {(data) => (
              <ul className="flex flex-col gap-3">
                {data.items.map((m, i) => {
                  const prev = data.items[i + 1];
                  return (
                    <li key={m.id}>
                      <MeasurementCard
                        dateLabel={formatDate(`${m.measuredOn}T00:00:00.000Z`, locale)}
                        note={m.note}
                        rows={[
                          {
                            label: t('dash.weight'),
                            value: m.weightKg,
                            unit: 'kg',
                            delta:
                              prev?.weightKg != null && m.weightKg != null
                                ? m.weightKg - prev.weightKg
                                : null,
                          },
                          { label: t('progress.waist'), value: m.waistCm, unit: 'cm' },
                          { label: t('progress.chest'), value: m.chestCm, unit: 'cm' },
                          { label: t('progress.hips'), value: m.hipsCm, unit: 'cm' },
                          { label: t('progress.arm'), value: m.armCm, unit: 'cm' },
                          { label: t('progress.bodyFat'), value: m.bodyFatPct, unit: '%' },
                        ]}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </QueryBoundary>
        </section>

        <section>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="eyebrow">{t('progress.photos')}</h2>
            <Button size="sm" variant="secondary" onClick={() => setPhotoOpen(true)}>
              {t('progress.addPhoto')}
            </Button>
          </div>
          <QueryBoundary
            query={photos}
            skeleton={<SkeletonCard />}
            isEmpty={(d) => d.items.length === 0}
            empty={{
              message: t('empty.noPhotos'),
              actionLabel: t('empty.addFirstPhoto'),
              onAction: () => setPhotoOpen(true),
            }}
          >
            {(data) => {
              /*
                Usporedba se gradi iz DATUMA, ne iz redoslijeda odgovora: ruta
                vraća silazno po `taken_on`, pa je „prva" stavka najnovija.
              */
              const pair = comparePair(data.items);
              return (
                <div className="flex flex-col gap-4">
                  {/*
                    Jedna fotografija NIJE prazno stanje. Prije je prag bio `< 2`,
                    pa je klijent koji je poslao prvu sliku dobio poruku da
                    fotografija nema — i pomislio da mu se prijenos nije spremio.
                  */}
                  {pair ? (
                    <PhotoCompare
                      before={{
                        url: pair.oldest.url,
                        caption: photoCaption(pair.oldest, t, locale),
                      }}
                      after={{
                        url: pair.newest.url,
                        caption: photoCaption(pair.newest, t, locale),
                      }}
                      labels={{ before: t('progress.before'), now: t('progress.now') }}
                      unavailableLabel={t('progress.photoUnavailable')}
                    />
                  ) : (
                    <p className="text-[14.5px] leading-relaxed text-ink-muted">
                      {t('empty.onePhotoOnly')}
                    </p>
                  )}

                  {/* Cijeli niz, najnoviji prvi — bez njega treća slika nigdje ne postoji. */}
                  <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {data.items.map((p) => (
                      <li key={p.id}>
                        <figure className="m-0">
                          <img
                            src={p.url}
                            alt={photoCaption(p, t, locale)}
                            loading="lazy"
                            className="aspect-[3/4] w-full rounded-md object-cover"
                          />
                          <figcaption className="mt-1 text-[12px] leading-snug text-ink-muted">
                            {photoCaption(p, t, locale)}
                          </figcaption>
                        </figure>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }}
          </QueryBoundary>
        </section>
      </div>

      <ProgressPhotoModal
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        onUploaded={() => {
          setPhotoOpen(false);
          toast(t('progress.photoUploaded'), 'success');
        }}
      />

      <Modal
        open={open}
        onClose={() => {
          setFormError(null);
          setOpen(false);
        }}
        title={t('progress.addMeasurement')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={addMeasurement.isPending} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p role="alert" className="text-[13px] text-danger">
              {formError}
            </p>
          )}
          {/* Uputa ide iznad polja: prvo se nauči kako mjeriti, pa se onda mjeri. */}
          <QueryBoundary query={guide} skeleton={<SkeletonCard />}>
            {(data) => <MeasurementGuide guide={data} />}
          </QueryBoundary>

          <NumberInput
            id="m-weight"
            label={t('dash.weight')}
            suffix="kg"
            step={0.1}
            value={weight}
            onValueChange={setWeight}
          />

          {CIRCUMFERENCE_FIELDS.map(({ field, labelKey, stepKey }) => (
            <NumberInput
              key={field}
              id={`m-${field}`}
              label={t(labelKey)}
              suffix="cm"
              step={0.5}
              value={circumference[field]}
              onValueChange={(v) => setCircumference((c) => ({ ...c, [field]: v }))}
              {...(guideStepText(guide.data, stepKey, locale)
                ? { hint: guideStepText(guide.data, stepKey, locale) }
                : {})}
            />
          ))}
        </div>
      </Modal>
    </>
  );
}
